import { InputBase, InputBaseProps, Skeleton, TextField, TextFieldProps } from "@mui/material";
import { forwardRef, ReactNode, useRef } from "react";
import { FormControlTooltip, FormControlTooltipProps } from "../tooltip";
import { CoreErrorMessage } from "./ErrorMessage";
import styles from "./TextField.module.css";
import CaretUp from "./images/arrow-up.svg?react";
import CaretDown from "./images/arrow-down.svg?react";

export type CoreTextFieldProps = Omit<
	TextFieldProps,
	"error" | "size" | "inputProps" | "aria-label" | "aria-labelledby"
> & {
	size?: "small" | "medium" | "large";
	error?: ReactNode;
	label?: string;
	useFloatingLabel?: boolean;
	ariaLabel?: string;
	ariaLabelledBy?: string;
	maxLength?: number;
	alwaysShowLength?: boolean;
	inputProps?: Omit<Exclude<TextFieldProps["inputProps"], undefined>, "maxLength">;
	/**@default "light" */
	colorVariant?: "dark" | "light";
} & Pick<FormControlTooltipProps<false>, "disabledTooltip" | "slotProps">;

export const CoreInputBase = forwardRef<
	HTMLDivElement,
	Omit<InputBaseProps, "size"> & {
		size?: "small" | "medium" | "large";
	}
>(function CoreInputBase({ size = "large", className, ...inputProps }, ref) {
	const { multiline } = inputProps;
	return (
		<InputBase
			className={`${styles.input} ${multiline ? "" : styles[size]} ${className || ""}`}
			{...inputProps}
			ref={ref}
		/>
	);
});

function LengthIndicator({ length, maxLength }: { length: number; maxLength: number }) {
	return (
		<div className={styles.lengthIndicator}>
			{length}/{maxLength}
		</div>
	);
}

const CoreTextField = forwardRef<HTMLDivElement, CoreTextFieldProps>(function CoreTextField(
	{
		disabledTooltip,
		size = "large",
		maxLength,
		alwaysShowLength,
		error,
		className,
		slotProps,
		InputProps,
		inputProps,
		label,
		useFloatingLabel,
		ariaLabel,
		ariaLabelledBy,
		colorVariant = "light",
		...textFieldProps
	},
	ref,
) {
	const { multiline, helperText, value, type } = textFieldProps;
	const currLength = typeof value === "string" ? value.length : undefined;
	const showLengthIndicator = maxLength != null && (alwaysShowLength || currLength != null);

	const inputRef = useRef<HTMLInputElement>(null);

	const isNumberInput = type === "number";

	// Mimic the native stepUp and stepDown methods for number inputs,
	const handleStep = (direction: "up" | "down") => {
		const input = inputRef.current;
		if (!input) return;

		if (direction === "up") input.stepUp();
		else input.stepDown();

		// call the original setter to make React realize the value changed.
		// eslint-disable-next-line @typescript-eslint/unbound-method
		const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;

		nativeInputValueSetter?.call(input, input.value);

		// Dispatch a single 'input' event (which React maps to onChange)
		const event = new Event("input", { bubbles: true });
		input.dispatchEvent(event);
	};

	// AutoComplete injects inputProps directly into Textfield which breaks the merging of InputProps, so explicitly merge props here
	// Merging InputProps.inputProps
	const mergedInputProps: TextFieldProps["inputProps"] = {
		...inputProps,
		...InputProps?.inputProps,
	};

	// Disable the spinner buttons once the value has hit the input's min/max, rather than letting
	// them sit there clickable but no-op.
	const numericValue = value !== undefined && value !== null && value !== "" ? Number(value) : undefined;
	const numericMin = mergedInputProps.min !== undefined ? Number(mergedInputProps.min) : undefined;
	const numericMax = mergedInputProps.max !== undefined ? Number(mergedInputProps.max) : undefined;
	const isAtMin = numericValue !== undefined && numericMin !== undefined && numericValue <= numericMin;
	const isAtMax = numericValue !== undefined && numericMax !== undefined && numericValue >= numericMax;

	// Merging InputProps
	const mergedOuterInputProps: TextFieldProps["InputProps"] = {
		...InputProps,
		endAdornment: (
			<>
				{InputProps?.endAdornment}
				{isNumberInput && (
					<div className={styles.customSpinnerWrapper}>
						<button
							type="button"
							className={styles.spinnerArrowButton}
							onClick={() => handleStep("up")}
							disabled={isAtMax}
							tabIndex={-1} // Don't trap focus while tabbing
						>
							<CaretUp className={styles.spinnerIcon} />
						</button>
						<button
							type="button"
							className={styles.spinnerArrowButton}
							onClick={() => handleStep("down")}
							disabled={isAtMin}
							tabIndex={-1}
						>
							<CaretDown className={styles.spinnerIcon} />
						</button>
					</div>
				)}
				{showLengthIndicator && <LengthIndicator length={currLength ?? 0} maxLength={maxLength} />}
			</>
		),
		className: `${styles.input} ${multiline ? "" : styles[size]} ${showLengthIndicator ? styles.limited : ""} ${InputProps?.className || ""}`,
		inputProps: {
			...mergedInputProps,
			maxLength,
			// Name the input/textarea rather than MUI's FormControl root (a div) — that's the element
			// that gets `placeholder` and the one assistive tech announces. Use the ariaLabel/
			// ariaLabelledBy props rather than plain aria-* attributes, which would land on the root.
			// Most specific wins: inputProps targets the input directly, then the component's own
			// ariaLabel prop, then the visible label as a fallback.
			"aria-label": mergedInputProps["aria-label"] ?? ariaLabel ?? label,
			"aria-labelledby": mergedInputProps["aria-labelledby"] ?? ariaLabelledBy,
			className: `${styles.input} ${isNumberInput ? styles.hideNativeArrows : ""} ${mergedInputProps.className || ""}`,
		},
	};

	return (
		<FormControlTooltip disabledTooltip={disabledTooltip} wrapDisabledChild={false} slotProps={slotProps}>
			<TextField
				placeholder={useFloatingLabel ? undefined : label}
				label={useFloatingLabel ? label : undefined}
				{...textFieldProps}
				className={`${styles.textField} ${colorVariant === "dark" ? styles.dark : ""} ${className || ""}`}
				error={!!error}
				InputProps={mergedOuterInputProps}
				helperText={error && typeof error !== "boolean" ? <CoreErrorMessage>{error}</CoreErrorMessage> : helperText}
				ref={ref}
				inputRef={inputRef}
			/>
		</FormControlTooltip>
	);
});
export default CoreTextField;

export function CoreTextFieldSkeleton({
	size = "large",
	fullWidth,
	sx,
	className,
}: Pick<CoreTextFieldProps, "size" | "fullWidth" | "sx" | "className">) {
	let height: number;
	switch (size) {
		case "small":
			height = 35;
			break;
		case "medium":
			height = 40;
			break;
		case "large":
			height = 47;
			break;
	}

	return (
		<Skeleton
			className={className}
			variant="rounded"
			height={height}
			width={fullWidth ? "100%" : undefined}
			sx={{ margin: "2px", ...sx }}
		/>
	);
}
